import type {
  IsValidFn,
  ToDateFn,
  FormatFn,
  FromNowFn,
  ToCalendarFn,
  HumanizeDateFn,
  HumanizeDaysFn,
  DaysToRoundedYearsFn,
  YearsToDaysFn,
  DateAddedDaysFn,
  DateAddedMonthsFn,
  SortDatesFn,
  IsInDateRangeFn,
  FormatDurationFn,
  DateValue,
  GetDateFromSearchParamsProps,
} from "./date.type";
import type { DateISO8601, DateISODate } from "@/types";

import dayjs from "dayjs";
import isEqual from "lodash/isEqual";

import { DAYSJS_DEFAULT_DATE_FORMAT } from "@/services/dayjs";

export const isValid: IsValidFn = (value) => {
  return value ? dayjs(value).isValid() : false;
};

/**
 * @deprecated Use `parseDate` instead
 */
export const toDate: ToDateFn = (value) => {
  // Handle undefined or null values safely
  if (!value) return dayjs(); // Return current date if value is falsy
  return isValid(value) ? dayjs(value) : dayjs(); // Return current date if value is invalid
};

export const parseDate = (value: DateValue) => {
  return isValid(value) ? dayjs(value) : null;
};

export const format: FormatFn = (
  value,
  format = DAYSJS_DEFAULT_DATE_FORMAT,
  locale
) => {
  // Return null for falsy values
  if (!value) return null;

  // If the value is already in YYYY-MM-DD format and no custom format is provided or the format is YYYY-MM-DD,
  // return it as is
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    (format === "YYYY-MM-DD" || format === undefined)
  ) {
    return value;
  }

  if (!isValid(value)) return null;

  if (!locale) return dayjs(value).format(format);

  return dayjs(value).locale(locale).format(format);
};

export const fromNow: FromNowFn = (value, withoutSuffix = false) =>
  value && isValid(value) ? dayjs(value).fromNow(withoutSuffix) : null;

export const toCalendar: ToCalendarFn = (value) =>
  value && isValid(value) ? dayjs(value).calendar() : null;

export const toISOString = (value: DateValue) =>
  value && isValid(value) ? (dayjs(value).toISOString() as DateISO8601) : null;

export const toDateString = (value: DateValue) =>
  value && isValid(value) ? (format(value, "YYYY-MM-DD") as DateISODate) : null;

export const humanizeDate: HumanizeDateFn = (date) => {
  const today = dayjs();
  const isFutur = dayjs(date).isAfter(today);
  return isFutur ? dayjs(date).fromNow() : today.to(dayjs(date));
};

export const humanizeDays: HumanizeDaysFn = (
  numberOfDays,
  withSuffix = false
) => {
  if (typeof numberOfDays !== "number") return "";
  return dayjs.duration(numberOfDays, "days").humanize(withSuffix);
};

export const daysToRoundedYears: DaysToRoundedYearsFn = (numberOfDays) => {
  if (typeof numberOfDays !== "number") {
    return null;
  }

  const yearsAsFloat = dayjs.duration(numberOfDays, "days").asYears();
  return Math.round(yearsAsFloat);
};

export const yearsToDays: YearsToDaysFn = (numberOfYears) => {
  if (typeof numberOfYears !== "number") {
    return null;
  }

  return Math.round(dayjs.duration(numberOfYears, "years").asDays());
};

export const dateAddedDays: DateAddedDaysFn = (numberOfDays, date) => {
  return format(dayjs(date ?? undefined).add(numberOfDays, "days"));
};

export const dateAddedMonths: DateAddedMonthsFn = (numberOfMonths, date) => {
  return format(dayjs(date ?? undefined).add(numberOfMonths, "months"));
};

export const sortDates: SortDatesFn = (date1, date2, order = "asc") => {
  if (!isValid(date1) || !isValid(date2)) {
    throw new Error("Invalid date provided for comparison");
  }

  const d1 = dayjs(date1);
  const d2 = dayjs(date2);

  if (d1.isSame(d2)) return 0;

  return order === "asc" ? (d1.isBefore(d2) ? -1 : 1) : d1.isAfter(d2) ? -1 : 1;
};

export const isInDateRange: IsInDateRangeFn = (
  date,
  { startDate, endDate, isInclusive }
) => {
  const inclusivity = isInclusive ? "[]" : undefined;
  return dayjs(date).isBetween(startDate, endDate, null, inclusivity);
};

export const formatDuration: FormatDurationFn = (ms, format = "HH:mm:ss") => {
  return dayjs.duration(ms, "milliseconds").format(format);
};

export const isSameDayInString = (date1: DateValue, date2: DateValue) => {
  return isEqual(format(date1), format(date2));
};

export const getDateFromSearchParams = ({
  key,
  params,
  format: formatString,
  locale,
}: GetDateFromSearchParamsProps) => {
  const stringValue = params.get(key);

  return format(stringValue, formatString, locale);
};
